<template>
  <div class="email-inbox">
    <div class="inbox-header">
      <h1>Email Inbox</h1>
      <div class="header-actions">
        <v-btn
          color="primary"
          @click="refreshEmails"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <v-row>
      <!-- Folder List -->
      <v-col cols="12" md="3">
        <v-card>
          <v-list>
            <v-list-item
              v-for="folder in folders"
              :key="folder.id"
              @click="selectFolder(folder)"
              :class="{ active: selectedFolder?.id === folder.id }"
            >
              <v-list-item-icon>
                <v-icon>{{ folder.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ folder.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ folder.unreadCount }} unread
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Email List -->
      <v-col cols="12" md="9">
        <v-card>
          <v-card-title>
            {{ selectedFolder?.name || 'Inbox' }}
            <v-spacer />
            <v-text-field
              v-model="searchQuery"
              append-icon="mdi-magnify"
              label="Search emails"
              single-line
              hide-details
              dense
            />
          </v-card-title>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <!-- Email List -->
          <v-list v-else-if="filteredEmails.length > 0" two-line>
            <v-list-item
              v-for="email in filteredEmails"
              :key="email.id"
              @click="viewEmail(email)"
              :class="{ unread: !email.read }"
            >
              <v-list-item-avatar>
                <v-icon>mdi-email{{ email.read ? '-open' : '' }}</v-icon>
              </v-list-item-avatar>
              
              <v-list-item-content>
                <v-list-item-title>
                  {{ email.subject }}
                  <v-chip v-if="email.folder === 'support'" x-small color="error" class="ml-2">Support</v-chip>
                  <v-chip v-if="email.folder === 'sales'" x-small color="success" class="ml-2">Sales</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <strong>{{ email.from }}</strong> - {{ email.preview }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-list-item-action-text>
                  {{ formatDate(email.receivedAt) }}
                </v-list-item-action-text>
                <div class="email-actions">
                  <v-btn icon small @click.stop="toggleStar(email)">
                    <v-icon small :color="email.starred ? 'yellow' : 'grey'">
                      mdi-star{{ email.starred ? '' : '-outline' }}
                    </v-icon>
                  </v-btn>
                  <v-btn icon small @click.stop="archiveEmail(email)">
                    <v-icon small>mdi-archive</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <!-- Empty State -->
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey">mdi-email-open-outline</v-icon>
            <p class="text-h6 mt-4">No emails in {{ selectedFolder?.name || 'this folder' }}</p>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Email Detail Dialog -->
    <v-dialog v-model="emailDialog" max-width="800">
      <v-card v-if="selectedEmail">
        <v-card-title>
          {{ selectedEmail.subject }}
          <v-spacer />
          <v-btn icon @click="emailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-subtitle>
          <div class="email-meta">
            <div><strong>From:</strong> {{ selectedEmail.from }}</div>
            <div><strong>To:</strong> {{ selectedEmail.to }}</div>
            <div><strong>Date:</strong> {{ formatDateTime(selectedEmail.receivedAt) }}</div>
          </div>
        </v-card-subtitle>

        <v-divider />

        <v-card-text>
          <div class="email-body" v-html="selectedEmail.body"></div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn text color="primary" @click="replyToEmail">
            <v-icon left>mdi-reply</v-icon>
            Reply
          </v-btn>
          <v-btn text @click="forwardEmail">
            <v-icon left>mdi-forward</v-icon>
            Forward
          </v-btn>
          <v-spacer />
          <v-btn text color="error" @click="deleteEmail">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useEmailStore } from '@/store/modules/email'

const emailStore = useEmailStore()

// State
const loading = ref(false)
const searchQuery = ref('')
const selectedFolder = ref(null)
const emailDialog = ref(false)
const selectedEmail = ref(null)

// Computed
const emails = computed(() => emailStore.emails)
const folders = computed(() => emailStore.folders)

const filteredEmails = computed(() => {
  let filtered = emails.value

  // Filter by folder
  if (selectedFolder.value) {
    if (selectedFolder.value.id === 'all') {
      // Show all emails
    } else if (selectedFolder.value.id === 'starred') {
      filtered = filtered.filter(e => e.starred)
    } else {
      filtered = filtered.filter(e => e.folder === selectedFolder.value.id)
    }
  }

  // Filter by search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(e => 
      e.subject.toLowerCase().includes(query) ||
      e.from.toLowerCase().includes(query) ||
      e.preview.toLowerCase().includes(query)
    )
  }

  return filtered
})

// Methods
const refreshEmails = async () => {
  loading.value = true
  try {
    await emailStore.fetchEmails()
  } finally {
    loading.value = false
  }
}

const selectFolder = (folder) => {
  selectedFolder.value = folder
}

const viewEmail = async (email) => {
  selectedEmail.value = email
  emailDialog.value = true
  
  // Mark as read
  if (!email.read) {
    await emailStore.markAsRead(email.id)
  }
}

const toggleStar = async (email) => {
  await emailStore.toggleStar(email.id)
}

const archiveEmail = async (email) => {
  await emailStore.archiveEmail(email.id)
}

const deleteEmail = async () => {
  if (selectedEmail.value) {
    await emailStore.deleteEmail(selectedEmail.value.id)
    emailDialog.value = false
  }
}

const replyToEmail = () => {
  // TODO: Implement reply functionality
  console.log('Reply to:', selectedEmail.value)
}

const forwardEmail = () => {
  // TODO: Implement forward functionality
  console.log('Forward:', selectedEmail.value)
}

const formatDate = (date) => {
  const d = new Date(date)
  const today = new Date()
  
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }
  
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatDateTime = (date) => {
  return new Date(date).toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Lifecycle
onMounted(() => {
  refreshEmails()
  // Select inbox by default
  selectedFolder.value = folders.value.find(f => f.id === 'inbox')
})
</script>

<style scoped>
.email-inbox {
  padding: 20px;
}

.inbox-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.inbox-header h1 {
  font-size: 2rem;
  font-weight: 500;
}

.v-list-item.active {
  background-color: #e3f2fd;
}

.v-list-item.unread {
  font-weight: bold;
  background-color: #f5f5f5;
}

.email-actions {
  display: flex;
  gap: 4px;
}

.email-meta {
  line-height: 1.8;
}

.email-body {
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;
}

.v-chip {
  height: 20px;
}
</style>